import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import config from '../../config.js';
import UserContext from '../common/UserContext.js';

// react router
import {useHistory} from 'react-router-dom';

// css
import '../../assets/css/Surveys.css';

// reactstrap
import { Col, Row } from 'reactstrap';

// kendo react
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TextArea } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';

// components
import Spinner from '../common/Spinner.js';

// multilingual
import { useLocalization } from '@progress/kendo-react-intl';
import {
    notNowKey,
    submitRequestKey,
    stronglyDisagreeKey,
    neutralKey,
    stronglyAgreeKey,
    easyKey,
    hardKey,
    mainMessages,
    pleaseShareFeedbackKey,
    pleaseTypeHereKey,
    shareFeedbackKey,
    howMuchAgreeKey,
    requestLicenseSurveyKey,
    softwareUpdatesSurveyKey,
    manageHostsSurveyKey,
    softwareAccessCheckoutKey,
    softwareAccessCheckinKey,
    softwareAccessViewDownloadKey,
} from '../../assets/text/MultilingualText.js';


const surveyConfig = {
    request_license: {
        questions: [
            howMuchAgreeKey,
            requestLicenseSurveyKey
        ],
        scale: {
            1: stronglyDisagreeKey,
            4: neutralKey,
            7: stronglyAgreeKey,
        }
    },
    fsa_request_license: {
        questions: [
            howMuchAgreeKey,
            requestLicenseSurveyKey
        ],
        scale: {
            1: stronglyDisagreeKey,
            4: neutralKey,
            7: stronglyAgreeKey,
        }
    },
    software_updates: {
        questions: [softwareUpdatesSurveyKey],
        scale: {
            1: hardKey,
            4: neutralKey,
            7: easyKey,
        }
    },
    manage_hosts: {
        questions: [manageHostsSurveyKey],
        scale: {
            1: hardKey,
            4: neutralKey,
            7: easyKey,
        }
    },
    software_access_checkout: {
        questions: [softwareAccessCheckoutKey],
        scale: {
            1: hardKey,
            4: neutralKey,
            7: easyKey,
        }
    },
    software_access_checkin: {
        questions: [softwareAccessCheckinKey],
        scale: {
            1: hardKey,
            4: neutralKey,
            7: easyKey,
        }
    },
    software_access_view_download: {
        questions: [softwareAccessViewDownloadKey],
        scale: {
            1: hardKey,
            4: neutralKey,
            7: easyKey,
        }
    },
};


function Survey(props) {
    const {
        accessToken,
        siteLanguageDefault,
        userEmail,
        surveyState,
        setSurveyState
    } = useContext(UserContext);
    const localization = useLocalization();
    const history = useHistory();
    const survey = surveyConfig[surveyState.module] || {};

    const [isLoading, setIsLoading] = useState(false);

    const [rating, setRating] = useState(null);
    const [feedback, setFeedback] = useState('');

    /*
    * submitSurvey() submits the survey
    * @param {cancelled} true if user closes or cancels survey
    */
    const submitSurvey = (cancelled = false) => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let data = {
            customer_email: userEmail,
            ratings: cancelled ? 0 : rating,
            feedback: cancelled ? "" : feedback,
            cancelled: cancelled ? "Y" : "N",
            module_type: surveyState.module.toUpperCase(),
        };

        // module-specific data
        if (surveyState.module === "request_license" || surveyState.module ===  "fsa_request_license") {
            data.transaction_id = cancelled ? [] : surveyState.data;
        } else if (surveyState.module === "software_updates") {
            data.update_id = cancelled ? [] : surveyState.data;
        } else if (surveyState.module === "manage_hosts") {
            data.host_id = cancelled ? [] : surveyState.data;
        } else if (surveyState.module === "software_access_checkout") {
            data.license_id = cancelled ? [] : surveyState.data;
        } else if (surveyState.module === "software_access_checkin" || surveyState.module === "software_access_view_download") {
            data.rental_id = cancelled ? [] : surveyState.data;
        }

        setIsLoading(true);

        axios.post(
            config.surveys.CREATE,
            data,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                   console.log("SUCCESS: Survey submitted successfully");
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to POST Request License Survey", error);
            })
            .finally(() => {
                setIsLoading(false);
                setSurveyState(prev => ({
                    ...prev,
                    step: cancelled ? null : 'thank_you',
                    visible: !cancelled
                }));
            })
    }

    // handle back button
    useEffect(() => {
        const unlisten = history.listen((location, action) => {
            if (action === "POP") {
                submitSurvey(true);
            }
        });
        return () => {
            unlisten();
        };
    }, [history]);

    return (
        <Dialog
            className={"survey-modal ksm-dialog"}
            title={
                <div className={"survey-title"}>
                    {localization.toLanguageString(shareFeedbackKey, mainMessages[siteLanguageDefault][shareFeedbackKey])}
                </div>
            }
            onClose={() => submitSurvey(true)}
        >
            {isLoading ? <Spinner/> : <></>}
            {survey.questions?.map((questionKey, index) => (
                <Row className={index > 0 ? "mt-3" : ""} key={index}>
                    <Col>
                        <Label>
                            {localization.toLanguageString(
                                questionKey,
                                mainMessages[siteLanguageDefault][questionKey]
                            )}
                        </Label>
                    </Col>
                </Row>
            ))}
            <Row className="rating-row">
                <div className="rating-scale">
                    {[1, 2, 3, 4, 5, 6, 7].map((value) => (
                        <div className="rating-wrapper" key={value}>
                            <div className="rating-item">
                                <button
                                    className={`rating-button ${rating === value ? "active" : ""}`}
                                    onClick={() => setRating(value)}
                                >
                                    {value}
                                </button>
                                <div className="rating-label-container">
                                    {survey.scale?.[value] && (
                                        <span className="rating-sublabel">
                                            {localization.toLanguageString(
                                                survey.scale[value],
                                                mainMessages[siteLanguageDefault][survey.scale[value]]
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {value < 7 && <span className="rating-spacing"></span>}
                        </div>
                    ))}
                </div>
            </Row>
            <Row>
                <Col>
                    <Label>
                        {localization.toLanguageString(pleaseShareFeedbackKey, mainMessages[siteLanguageDefault][pleaseShareFeedbackKey])}
                    </Label>
                    <TextArea
                        value={feedback}
                        placeholder={localization.toLanguageString(pleaseTypeHereKey, mainMessages[siteLanguageDefault][pleaseTypeHereKey])}
                        maxLength={500}
                        rows={4}
                        onChange={(e) => setFeedback(e.value)}
                        autoSize={false}
                        style={{ width: '100%' }}
                    />
                </Col>
            </Row>
            <DialogActionsBar layout="end">
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"outline"}
                    type={"button"}
                    onClick={() => submitSurvey(true)}
                >
                    {localization.toLanguageString(notNowKey, mainMessages[siteLanguageDefault][notNowKey])}
                </Button>
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"solid"}
                    type={"button"}
                    onClick={() => submitSurvey(false)}
                    disabled={rating === null}
                >
                    {localization.toLanguageString(submitRequestKey, mainMessages[siteLanguageDefault][submitRequestKey])}
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
}

export default Survey;